import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import axios from "axios";
import API_ENDPOINTS from "../config/api";
import ConfirmInfoModal from "../components/ConfirmInfoModal";

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// 컨텐츠 애니메이션 변형 설정
const contentVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.2,
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// 버튼 애니메이션 변형 설정
const buttonVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.6,
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

function ApplicantInfoPageV2() {
  const navigate = useNavigate();
  const location = useLocation();

  // 쿠키에서 동, 호수 정보 가져오기
  const getBuildingUnitInfo = () => {
    const cookieData = Cookies.get("buildingUnitInfo");
    if (cookieData) {
      try {
        return JSON.parse(cookieData);
      } catch (error) {
        console.error("쿠키 파싱 오류:", error);
      }
    }
    return location.state || {};
  };

  const buildingUnitInfo = getBuildingUnitInfo();
  const { building, unit } = buildingUnitInfo;

  // CoOwnerCheckPage에서 전달받은 공동소유자 여부
  const { hasCoOwner } = location.state || {};

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    birthDate: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    phone: "",
    birthDate: "",
  });

  const [isGeneratingDelegation, setIsGeneratingDelegation] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // 유효한 날짜인지 확인하는 함수
  const isValidDate = (dateString) => {
    if (!/^\d{8}$/.test(dateString)) return false;

    const year = parseInt(dateString.substring(0, 4));
    const month = parseInt(dateString.substring(4, 6));
    const day = parseInt(dateString.substring(6, 8));

    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear) return false;

    if (month < 1 || month > 12) return false;

    const lastDayOfMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > lastDayOfMonth) return false;

    return true;
  };

  // 전화번호 형식 검증 함수 (하이픈 없이)
  const isValidPhone = (phone) => {
    const phoneRegex = /^010\d{8}$/;
    return phoneRegex.test(phone.replace(/\s/g, ""));
  };

  // 전화번호 자동 포맷팅 함수 (하이픈 제거)
  const formatPhoneNumber = (value) => {
    return value.replace(/[^\d]/g, "").slice(0, 11);
  };

  const handleInputChange = (field, value) => {
    if (field === "name") {
      // 이름은 실시간 검증하지 않고 에러 초기화만
      setErrors((prev) => ({
        ...prev,
        name: "",
      }));
    } else if (field === "phone") {
      value = formatPhoneNumber(value);

      // 연락처가 11자리가 되면 키보드 닫기
      if (value.replace(/[^\d]/g, "").length === 11) {
        // 입력 필드에서 포커스 제거하여 키보드 닫기
        setTimeout(() => {
          const phoneInput = document.querySelector('input[name="phone"]');
          if (phoneInput) {
            phoneInput.blur();
          }
        }, 100);
      }

      // 전화번호 실시간 검증
      if (value.length === 11) {
        if (!isValidPhone(value)) {
          setErrors((prev) => ({
            ...prev,
            phone: "올바른 전화번호를 입력해주세요.\n(010으로 시작하는 11자리)",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            phone: "",
          }));
        }
      } else {
        setErrors((prev) => ({
          ...prev,
          phone: "",
        }));
      }
    } else if (field === "birthDate") {
      value = value.replace(/[^\d]/g, "").slice(0, 8);

      // 생년월일이 8자리가 되면 키보드 닫기
      if (value.length === 8) {
        // 입력 필드에서 포커스 제거하여 키보드 닫기
        setTimeout(() => {
          const birthDateInput = document.querySelector(
            'input[name="birthDate"]'
          );
          if (birthDateInput) {
            birthDateInput.blur();
          }
        }, 100);
      }

      // 생년월일 실시간 검증
      if (value.length === 8) {
        if (!isValidDate(value)) {
          setErrors((prev) => ({
            ...prev,
            birthDate: "올바른 생년월일을 입력해주세요. (YYYYMMDD)",
          }));
        } else if (isMinor(value)) {
          setErrors((prev) => ({
            ...prev,
            birthDate: "미성년자는 오프라인신청대상입니다.",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            birthDate: "",
          }));
        }
      } else {
        setErrors((prev) => ({
          ...prev,
          birthDate: "",
        }));
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // 미성년자 검증 함수 (2007년 1월 1일 이후 출생자는 미성년자)
  const isMinor = (birthDate) => {
    if (!/^\d{8}$/.test(birthDate)) return false;

    const year = parseInt(birthDate.substring(0, 4));
    const month = parseInt(birthDate.substring(4, 6));
    const day = parseInt(birthDate.substring(6, 8));

    const birthDateObj = new Date(year, month - 1, day);
    const cutoffDate = new Date(2007, 0, 1); // 2007년 1월 1일

    return birthDateObj >= cutoffDate;
  };

  const validateForm = () => {
    const newErrors = {};

    // 이름 검증
    if (!formData.name.trim()) {
      newErrors.name = "이름을 입력해주세요.";
    } else {
      // 자음이나 모음만 있는 경우 검증 (모바일 호환성 개선)
      const hasIncompleteHangul = /^[ㄱ-ㅎㅏ-ㅣ]+$/.test(formData.name.trim());
      if (hasIncompleteHangul) {
        newErrors.name = "이름을 정확히 입력해주세요.";
      } else {
        // 한글이 아닌 문자 검증 (한글만 허용)
        const hasNonKoreanCharacters = /[^가-힣\s]/.test(formData.name.trim());
        if (hasNonKoreanCharacters) {
          newErrors.name = "이름은 한글로만 입력해주세요.";
        }
      }
    }

    // 전화번호 검증
    if (!formData.phone.trim()) {
      newErrors.phone = "전화번호를 입력해주세요.";
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone =
        "올바른 전화번호를 입력해주세요.\n(010으로 시작하는 11자리)";
    }

    // 생년월일 검증
    if (!formData.birthDate.trim()) {
      newErrors.birthDate = "생년월일을 입력해주세요.";
    } else if (!isValidDate(formData.birthDate)) {
      newErrors.birthDate = "올바른 생년월일을 입력해주세요. (YYYYMMDD)";
    } else if (isMinor(formData.birthDate)) {
      newErrors.birthDate = "미성년자는 오프라인신청대상입니다.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 위임장 생성 함수
  const handleGenerateDelegation = async () => {
    // 위임장 생성 버튼 클릭 시에도 동일한 검증 실행
    if (!validateForm()) {
      return;
    }

    setIsGeneratingDelegation(true);

    try {
      // API 요청 데이터 준비
      const requestData = {
        data: [
          {
            동: building?.trim() || "102",
            호수: unit?.trim() || "0000",
            이름: formData.name.trim(),
            생년월일: formData.birthDate.trim(),
          },
        ],
      };

      // API 호출
      const response = await fetch(
        "http://18.222.3.212:3000/api/generate-delegations-hwp2",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "위임장 생성에 실패했습니다.");
      }

      // ZIP 파일 다운로드
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "delegations_hwp2.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert("위임장이 성공적으로 생성되었습니다!");
    } catch (error) {
      console.error("위임장 생성 오류:", error);
      alert(`위임장 생성에 실패했습니다: ${error.message}`);
    } finally {
      setIsGeneratingDelegation(false);
    }
  };

  const handleNext = () => {
    // 다음 버튼 클릭 시에만 검증 실행
    if (validateForm()) {
      // 확인 모달 표시
      setShowConfirmModal(true);
    }
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false);
  };

  const handleConfirmModalConfirm = async () => {
    // 모달 닫기
    setShowConfirmModal(false);

    // 신청자 정보를 쿠키에 저장
    const currentBuildingUnitInfo = getBuildingUnitInfo();
    const updatedBuildingUnitInfo = {
      ...currentBuildingUnitInfo,
      applicantName: formData.name.trim(),
      applicantBirthDate: formData.birthDate.trim(),
      applicantPhone: formData.phone.trim(),
    };

    Cookies.set("buildingUnitInfo", JSON.stringify(updatedBuildingUnitInfo));

    try {
      // PUT /claim/assignment API 호출
      const assignmentData = {
        claimAssignmentName: formData.name.trim(),
        claimAssignmentPhone: formData.phone.trim(),
        claimAssignmentBirth: formData.birthDate.trim(),
      };

      const response = await axios.put(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim/assignment`,
        assignmentData
      );

      // 공동소유자 여부에 따라 다음 페이지 결정
      if (hasCoOwner) {
        navigate("/co-owner-info"); // 공동소유자가 있으면 공동소유자 정보 입력 페이지로
      } else {
        navigate("/bond-agreement"); // 공동소유자가 없으면 바로 동의 페이지로
      }
    } catch (error) {
      console.error("Claim Assignment API 호출 실패:", error);
      // API 호출 실패 시 toast 알림 표시하고 첫 번째 페이지로 이동
      alert("문제가 발생했습니다. 다시 시도해주세요.");
      navigate("/");
    }
  };

  const isFormValid = () => {
    return (
      formData.name.trim() && formData.phone.trim() && formData.birthDate.trim()
    );
  };

  const getApplicantInfoForModal = () => {
    return {
      totalCount: 1,
      coOwners: [
        {
          id: 1,
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          birthDate: formData.birthDate.trim(),
          relation: "신청인",
        },
      ],
    };
  };

  return (
    <motion.div
      className="page-container"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        padding: "0 24px",
        position: "relative",
        paddingBottom: "104px",
      }}
    >
      {/* 헤더 */}
      <div
        style={{
          marginTop: "20px",
        }}
        className="page-header"
      >
        <h1>신청인 정보 입력</h1>
      </div>

      <motion.div
        className="co-owner-info-content"
        variants={contentVariants}
        initial="initial"
        animate="animate"
        style={{
          marginTop: "20px",
          width: "100%",
          maxWidth: "460px",
          paddingBottom: "100px",
          overflowY: "auto",
          height: "100%",
        }}
      >
        <div className="info-section">
          <h2
            style={{
              fontSize: "22px",
              fontWeight: "700",
              color: "#2E3840",
              marginBottom: "16px",
            }}
          >
            신청인 정보입력
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "500",
              color: "#4B5563",
              marginBottom: "24px",
            }}
          >
            위임장 생성을 위해 신청인 정보를 입력해주세요.
          </p>

          <div
            style={{
              marginTop: "30px",
              width: "100%",
            }}
          >
            {/* 신청자 정보 입력 폼 */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <div
                style={{
                  marginBottom: "16px",
                  border: "1px solid #F3F4F6",
                  borderRadius: "8px",
                  overflow: "visible",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{
                    padding: "16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "#F9FAFB",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <img
                      src="/icon-park-outline_people.png"
                      alt="신청인 아이콘"
                      style={{
                        width: "20px",
                        height: "20px",
                      }}
                    />
                    <span style={{ fontWeight: "600" }}>신청인 정보</span>
                  </div>
                </div>

                <div
                  style={{
                    overflow: "visible",
                  }}
                >
                  <div
                    style={{
                      padding: "16px",
                    }}
                  >
                    {/* 이름 입력 필드 */}
                    <div style={{ marginBottom: "16px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#4B5563",
                        }}
                      >
                        이름<span style={{ color: "#FF4D4F" }}>*</span>
                      </label>
                      <input
                        type="text"
                        lang="ko"
                        value={formData.name}
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        placeholder="이름을 입력해주세요"
                        style={{
                          width: "100%",
                          padding: "12px",
                          border: errors.name
                            ? "2px solid #DC4B3F"
                            : "2px solid #F3F4F6",
                          borderRadius: "8px",
                          fontSize: "16px",
                          outline: "none",
                        }}
                        onFocus={(e) => {
                          if (!errors.name) {
                            e.target.style.borderColor = "#7A4FFF";
                            e.target.style.outline = "none";
                          }
                        }}
                        onBlur={(e) => {
                          if (!errors.name) {
                            e.target.style.borderColor = "#F3F4F6";
                            e.target.style.outline = "none";
                          }
                        }}
                      />
                      {errors.name && (
                        <div
                          className="error-message"
                          style={{
                            color: "#DC4B3F",
                            fontSize: "14px",
                            marginTop: "4px",
                          }}
                        >
                          {errors.name}
                        </div>
                      )}
                    </div>

                    {/* 전화번호 입력 필드 */}
                    <div style={{ marginBottom: "16px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#4B5563",
                        }}
                      >
                        연락처
                        <span style={{ color: "#FF4D4F" }}>*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        placeholder="핸드폰 번호를 입력해주세요 (- 없이)"
                        style={{
                          width: "100%",
                          padding: "12px",
                          border: errors.phone
                            ? "2px solid #DC4B3F"
                            : "2px solid #F3F4F6",
                          borderRadius: "8px",
                          fontSize: "16px",
                          outline: "none",
                        }}
                        onFocus={(e) => {
                          if (!errors.phone) {
                            e.target.style.borderColor = "#7A4FFF";
                            e.target.style.outline = "none";
                          }
                        }}
                        onBlur={(e) => {
                          if (!errors.phone) {
                            e.target.style.borderColor = "#F3F4F6";
                            e.target.style.outline = "none";
                          }
                        }}
                      />
                      {errors.phone && (
                        <div
                          className="error-message"
                          style={{
                            color: "#DC4B3F",
                            fontSize: "14px",
                            marginTop: "4px",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {errors.phone}
                        </div>
                      )}
                    </div>

                    {/* 생년월일 입력 필드 */}
                    <div
                      style={{
                        marginBottom: "16px",
                        position: "relative",
                        marginTop: "16px",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#4B5563",
                        }}
                      >
                        생년월일
                        <span style={{ color: "#FF4D4F" }}>*</span>
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="tel"
                          name="birthDate"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={formData.birthDate}
                          onChange={(e) =>
                            handleInputChange("birthDate", e.target.value)
                          }
                          placeholder="생년월일 8자리 (YYYYMMDD)"
                          style={{
                            width: "100%",
                            padding: "12px",
                            border: errors.birthDate
                              ? "2px solid #DC4B3F"
                              : "2px solid #F3F4F6",
                            borderRadius: "8px",
                            fontSize: "16px",
                            outline: "none",
                          }}
                          maxLength={8}
                          onFocus={(e) => {
                            if (!errors.birthDate) {
                              e.target.style.borderColor = "#7A4FFF";
                              e.target.style.outline = "none";
                            }
                          }}
                          onBlur={(e) => {
                            if (!errors.birthDate) {
                              e.target.style.borderColor = "#F3F4F6";
                              e.target.style.outline = "none";
                            }
                          }}
                        />
                      </div>
                      {errors.birthDate && (
                        <div
                          className="error-message"
                          style={{
                            color: "#DC4B3F",
                            fontSize: "14px",
                            marginTop: "4px",
                          }}
                        >
                          {errors.birthDate}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* 하단 버튼들 */}
      <motion.div
        style={{
          position: "fixed",
          bottom: -20,
          left: 0,
          right: 0,
          padding: "24px",
          background: "white",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
        variants={buttonVariants}
        initial="initial"
        animate="animate"
      >
        {/* 위임장 생성 버튼 */}
        {/* <button
          onClick={handleGenerateDelegation}
          disabled={!isFormValid() || isGeneratingDelegation}
          style={{
            width: "100%",
            maxWidth: "460px",
            padding: "12px",
            background:
              !isFormValid() || isGeneratingDelegation ? "#F3F4F6" : "#7C3AED",
            border: "none",
            borderRadius: "8px",
            color:
              !isFormValid() || isGeneratingDelegation ? "#9CA3AF" : "#FFFFFF",
            fontSize: "16px",
            fontWeight: "500",
            cursor:
              !isFormValid() || isGeneratingDelegation
                ? "not-allowed"
                : "pointer",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            marginTop: "-15px",
          }}
        >
          {isGeneratingDelegation ? "위임장 생성 중..." : "위임장 생성"}
        </button> */}

        {/* 다음 버튼 */}
        <button
          onClick={handleNext}
          disabled={!isFormValid()}
          style={{
            width: "100%",
            maxWidth: "460px",
            height: "56px",
            fontSize: "16px",
            fontWeight: "600",
            background: isFormValid() ? "#7A4FFF" : "#EBEBFF",
            color: isFormValid() ? "#FFFFFF" : "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: isFormValid() ? "pointer" : "not-allowed",
            transition: "background-color 0.2s ease",
            boxShadow: isFormValid()
              ? "0 2px 5px rgba(0, 117, 255, 0.2)"
              : "none",
            marginBottom: "30px",
          }}
        >
          다음
        </button>
      </motion.div>

      {/* 확인 모달 */}
      <ConfirmInfoModal
        isOpen={showConfirmModal}
        onClose={handleConfirmModalClose}
        onConfirm={handleConfirmModalConfirm}
        formData={getApplicantInfoForModal()}
        isApplicantInfo={true}
      />

      {/* 로딩 모달 */}
      {isGeneratingDelegation && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "80%",
              maxWidth: "320px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "25px",
                borderTop: "3px solid #7A4FFF",
                borderRight: "3px solid transparent",
                margin: "0 auto 16px",
                animation: "spin 1s linear infinite",
              }}
            ></div>
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#1F2937",
              }}
            >
              위임장 생성 중
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#6B7280",
              }}
            >
              잠시만 기다려주세요...
            </p>
          </div>
        </div>
      )}

      {/* 스피너 애니메이션 CSS */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </motion.div>
  );
}

export default ApplicantInfoPageV2;
